import {Button as AntdButton, message, Modal, Table, TablePaginationConfig} from 'antd'
import React, {useEffect, useState} from 'react'
import {
  useLocation,
  useNavigate,
  useRevalidator,
  useRouteLoaderData,
  useSearchParams,
} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {JournalFilters} from '../../services/JournalService'
import {BugOutlined, ScheduleOutlined, SearchOutlined} from '@ant-design/icons'
import {useAsyncValue} from '../../hooks/useAsyncValue'
import {instance as journalService} from '../../services/JournalService'
import type {ColumnsType} from 'antd/es/table'
import type {ColumnType, FilterValue, SorterResult} from 'antd/es/table/interface'
import {TableSearchFilter} from '../TableSearchFilter'
import {TableSearchDataFilter} from '../TableSearchDataFilter'
import {
  JournalRecord,
  EventTypeMetadata,
  JournalEventType,
  getJournalEventTypeInfo,
  CustomJournalEventType,
} from '../../entities/journalRecord'
import {JournalEventForm} from './JournalEventForm'
import {useMousePositionContext} from '../plantForm/usePlantModalMousePosition'
import {JournalEventModal} from './JournalEventModal'
import {PlantModal} from '../plantForm/PlantModal'
import {faTruckMedical} from '@fortawesome/free-solid-svg-icons'

interface TableParams {
  pagination?: TablePaginationConfig
  filters?: JournalFilters
}

export function JournalPage() {
  const {projectNames} = useRouteLoaderData('root') as {
    projectNames: {id: string; name: string}[]
  }

  const {plantedPlantsNamesWithId, userJournalEventTypes, userJournalEvents, tableParams} =
    useRouteLoaderData('journal') as {
      plantedPlantsNamesWithId: {id: number; name: string}[]
      userJournalEventTypes: CustomJournalEventType[]
      userJournalEvents: {count: number; data: JournalRecord[]}
      tableParams: {page: number; perPage: number; sortField: string; sortOrder: 'asc' | 'desc'}
    }

  const [openFormState, setOpenFormState] = useState<boolean>(false)
  const [currentRecord, setCurrentRecord] = useState<JournalRecord>(null)

  const revalidator = useRevalidator()

  const {setMousePosition} = useMousePositionContext()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  const intl = useIntl()
  const [messageApi, contextHolder] = message.useMessage()

  const columns: ColumnsType<JournalRecord> = [
    {
      title: intl.formatMessage({id: 'type'}),
      dataIndex: 'type',
      key: 'type',
      sorter: true,
      sortDirections: ['ascend', 'descend', null],
      render: (type) => (
        <span>
          {type
            ? `${getJournalEventTypeInfo(type).icon} ${
                type.type === 'custom'
                  ? getJournalEventTypeInfo(type).name
                  : intl.formatMessage({id: `eventType-${getJournalEventTypeInfo(type).name}`})
              }`
            : ''}
        </span>
      ),
      filters: [
        ...Object.entries(EventTypeMetadata).map(([key, meta]) => ({
          value: key,
          text: `${meta.icon} ${intl.formatMessage({id: `eventType-${meta.name}`})}`,
        })),
        ...userJournalEventTypes.map((userType, key) => ({
          value: userType.type === 'custom' ? userType.customJournalEventType.id : key,
          text: `${userType.customJournalEventType.icon} ${userType.customJournalEventType.name}`,
        })),
      ],
    },
    {
      title: intl.formatMessage({id: 'eventEndDate'}),
      dataIndex: 'actualCompletionDateUtc',
      key: 'actualCompletionDateUtc',
      sorter: true,
      sortDirections: ['ascend', 'descend', null],
      render: (value) => (value ? String(value) : ''),
      filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => {
        return (
          <div style={{padding: 8}}>
            <TableSearchDataFilter
              filterValue={selectedKeys[0] as string}
              onFilterValueSet={([startDate, endDate]) => {
                if (!startDate && !endDate) {
                  clearFilters()
                  confirm()
                } else {
                  setSelectedKeys([startDate, endDate])
                  confirm()
                }
              }}
            />
          </div>
        )
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}} />
      ),
    },
    {
      title: intl.formatMessage({id: 'plant'}),
      dataIndex: ['plant', 'name'],
      sorter: true,
      sortDirections: ['ascend', 'descend', null],
      key: 'plant',
      render: (plant) => <span>{plant ? plant : ''}</span>,
      filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => {
        return (
          <div style={{padding: 8}}>
            <TableSearchFilter
              filterValue={selectedKeys[0] as string}
              onFilterValueSet={(value) => {
                if (value === '') {
                  clearFilters()
                  confirm()
                } else {
                  setSelectedKeys([value])
                  confirm()
                }
              }}
            />
          </div>
        )
      },
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}} />
      ),
    },
    {
      title: intl.formatMessage({id: 'comment'}),
      dataIndex: 'comment',
      key: 'comment',
    },
    // {
    //   title: intl.formatMessage({id: 'project'}),
    //   dataIndex: ['project', 'name'],
    //   key: 'project',
    //   filters: projectNames.map((project) => ({
    //     value: project.id,
    //     text: project.name,
    //   })),
    // },
  ]

  return (
    <div>
      {contextHolder}
      <div>
        <h2>{intl.formatMessage({id: 'gardenJournal'})}</h2>
      </div>
      <div>
        <Table
          rowKey="id"
          dataSource={userJournalEvents.data}
          columns={columns.map(
            (column) =>
              ({
                ...column,
                sortOrder:
                  tableParams.sortField === column.key
                    ? tableParams.sortOrder === 'asc'
                      ? 'ascend'
                      : 'descend'
                    : null,
              } as ColumnType<JournalRecord>)
          )}
          onChange={(tableParams, filters, sorter) => {
            const page = tableParams.current
            const perPage = tableParams.pageSize

            const newSearchParams = new URLSearchParams()

            if (page !== 1) {
              newSearchParams.set('page', String(page))
            }

            if (perPage !== 10) {
              newSearchParams.set('perPage', String(perPage))
            }

            const filter = {
              actualCompletionDateUtc: filters.actualCompletionDateUtc
                ? {
                    startDate: filters.actualCompletionDateUtc[0]
                      ? new Date(filters.actualCompletionDateUtc[0] as string)
                      : undefined,
                    endDate: filters.actualCompletionDateUtc[1]
                      ? new Date(filters.actualCompletionDateUtc[1] as string)
                      : undefined,
                  }
                : undefined,
              eventType: filters.type?.length > 0 ? (filters.type as string[]) : undefined,
              plant: filters.plant ? (filters.plant[0] as string) : undefined,
              project: filters.project?.length > 0 ? (filters.project as string[]) : undefined,
            }
            const filterJson = JSON.stringify(filter)

            if (filterJson !== '{}') {
              newSearchParams.set('filter', filterJson)
            }

            const sortConfig = Array.isArray(sorter) ? sorter[0] : sorter
            const sortField = sortConfig.columnKey
            const sortOrder = sortConfig.order

            if (sortOrder && sortField) {
              newSearchParams.set('sortOrder', sortOrder === 'ascend' ? 'asc' : 'desc')
              newSearchParams.set('sortField', String(sortField))
            }

            setSearchParams(newSearchParams)
          }}
          pagination={{
            pageSize: tableParams.perPage,
            current: tableParams.page,
            total: userJournalEvents.count,
            showSizeChanger: true,
          }}
          scroll={{x: 'max-content'}}
          onRow={(record) => ({
            onClick: (e) => {
              setMousePosition({x: e.pageX, y: e.pageY})
              //setOpenFormState(true)
              navigate(`${record.id}` + location.search)
              setCurrentRecord(record)
            },
          })}
        ></Table>

        <JournalEventModal routeWhenClosed={'/journal' + location.search}></JournalEventModal>
        <PlantModal routeWhenClosed={`/journal` + location.search} />
      </div>
    </div>
  )
}
